import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import LoadingSpinner from './components/LoadingSpinner';
import Comments from './components/Comments';
import { usePlugYourWork } from './hooks/usePlugYourWork';
import { getAuthorName } from './utils/authorName';
import './PlugYourWork.css';

const PlugYourWork = () => {
  const [offset, setOffset] = useState(0);
  const [allEntries, setAllEntries] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  
  // Use React Query for caching
  const { data, isLoading, isError } = usePlugYourWork({ limit: 20, offset });
  
  // Update entries when data changes
  useEffect(() => {
    if (data) {
      if (offset === 0) {
        // Initial load
        setAllEntries(data.data || []);
      } else {
        // Load more - append to existing entries
        setAllEntries(prevEntries => [...prevEntries, ...(data.data || [])]);
      }
      
      // Check if there are more entries to load
      setHasMore((data.data || []).length === 20);
    }
  }, [data, offset]);

  const loadMore = () => {
    setOffset(prevOffset => prevOffset + 20);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Show loading spinner when loading, when there's an error, or when no data yet
  if (isLoading || isError || !data) {
    return (
      <div className="plug-your-work-container">
        <LoadingSpinner message="Loading featured work..." />
      </div>
    );
  }

  return (
    <div className="plug-your-work-container">
      <div className="plug-your-work-header">
        <h1>Featured work from Study Hall members</h1>
        <p className="entry-count">Here is some of what our community has been working on. Give them a read...and a share!<br /><br />Have something you would like to submit? <Link to="/plug-your-work/submit" style={{ color: '#38b44a', textDecoration: 'underline', fontWeight: '600' }}>Plug Your Work here</Link>.</p>
      </div>

      {allEntries.length > 0 && (
        <div className="entries-grid">
          {allEntries.map((entry) => (
            <div key={entry.id} className="entry-card">
              <div className="entry-header">
                <h3 className="entry-title">
                  {entry.headline || 'Untitled'}
                </h3>
                {entry.publication && (
                  <p className="entry-publication">{entry.publication}</p>
                )}
              </div>

              {entry.blurb && (
                <div className="entry-blurb">
                  <p>{entry.blurb}</p>
                </div>
              )}

              <div className="entry-footer">
                <div className="entry-meta">
                  <div className="entry-author-info">
                    <div className="author-avatar">
                      {entry.author_id ? (
                        <Link 
                          to={`/profile/${entry.author_id}`}
                          className="author-avatar-link"
                        >
                          <div className="avatar-placeholder">
                            {getAuthorName(entry).charAt(0).toUpperCase()}
                          </div>
                        </Link>
                      ) : (
                        <div className="avatar-placeholder">
                          {getAuthorName(entry).charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="author-details">
                      {getAuthorName(entry) && (
                        <span className="entry-author">
                          By {entry.author_id ? (
                            <Link 
                              to={`/profile/${entry.author_id}`}
                              className="entry-author-link"
                            >
                              {getAuthorName(entry)}
                            </Link>
                          ) : (
                            <span>{getAuthorName(entry)}</span>
                          )}
                        </span>
                      )}
                      {entry.posted && (
                        <span className="entry-date">{formatDate(entry.posted)}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  {entry.url && (
                    <a 
                      href={entry.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="read-article-button"
                    >
                      Read Article
                    </a>
                  )}
                </div>
              </div>
              
                      <Comments 
                        contentId={entry.id}
                        contentType="plug-your-work"
                        onCommentAdded={() => {}}
                      />
            </div>
          ))}
        </div>
      )}

      {hasMore && (
        <div className="load-more-container">
          <button 
            onClick={loadMore} 
            disabled={isLoading}
            className="load-more-button"
          >
            {isLoading ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}
    </div>
  );
};

export default PlugYourWork;
